import { cn } from '@popow/infra/lib/utils';
import React from 'react';

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 최대 너비 설정
   * @default 'screen-md'
   */
  maxWidth?: 'screen-sm' | 'screen-md' | 'screen-lg' | 'screen-xl' | 'full';

  /**
   * 배경색 설정
   * @default 'white'
   */
  backgroundColor?: 'white' | 'gray-50' | 'transparent';

  /**
   * 최소 높이 설정
   * @default 'screen'
   */
  minHeight?: 'screen' | 'auto' | 'full';

  /**
   * 패딩 설정
   * @default 'none'
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 자식 요소
   */
  children: React.ReactNode;
}

const maxWidthClasses = {
  'screen-sm': 'max-w-screen-sm',
  'screen-md': 'max-w-screen-md',
  'screen-lg': 'max-w-screen-lg',
  'screen-xl': 'max-w-screen-xl',
  full: 'max-w-full',
} as const;

const backgroundColorClasses = {
  white: 'bg-white',
  'gray-50': 'bg-gray-50',
  transparent: 'bg-transparent',
} as const;

const minHeightClasses = {
  screen: 'min-h-screen',
  auto: 'min-h-auto',
  full: 'min-h-full',
} as const;

const paddingClasses = {
  none: '',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
} as const;

export const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  (
    {
      maxWidth = 'screen-md',
      backgroundColor = 'white',
      minHeight = 'screen',
      padding = 'none',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses = 'mx-auto';

    const maxWidthClass = maxWidthClasses[maxWidth];
    const backgroundColorClass = backgroundColorClasses[backgroundColor];
    const minHeightClass = minHeightClasses[minHeight];
    const paddingClass = paddingClasses[padding];

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          maxWidthClass,
          backgroundColorClass,
          minHeightClass,
          paddingClass,
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

PageContainer.displayName = 'PageContainer';

export default PageContainer;
